import React from 'react';
import { Box, Container, Typography, Card, Grid, alpha, Button, Avatar, Chip } from '@mui/material';
import Settings from '@mui/icons-material/Settings';
import { formatProduct } from '../../../utils/utilfunction';
import { useTheme } from '@mui/material/styles';

const PreferencesTab = ({
  userSettings,
  handleOpenSettingsDialog
}) => {
  const theme = useTheme();

  return (
    <Container maxWidth="lg">
      <Box sx={{ 
        mb: 5, 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 700, 
            color: theme.palette.text.primary,
            fontSize: { xs: '1.5rem', sm: '1.75rem' },
            mb: { xs: 1, sm: 2 },
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 60,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.4)})`,
              borderRadius: 4,
            }
          }}
        >
          Trading Information
        </Typography>
      </Box>

      <Card 
        elevation={0}
        sx={{ 
          borderRadius: 4, 
          overflow: 'hidden',
          boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          transition: 'all 0.3s ease',
          mb: 4,
          backgroundImage: `linear-gradient(to bottom, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.background.paper, 0.9)})`,
          '&:hover': {
            boxShadow: '0 15px 40px rgba(0,0,0,0.15)',
            transform: 'translateY(-5px)'
          }
        }}
      >
        <Box 
          sx={{ 
            px: { xs: 3, md: 4 }, 
            py: 3, 
            background: theme.palette.mode === 'dark'
              ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.secondary.main, 0.9)})`
              : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box 
            sx={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.07,
              backgroundImage: 'radial-gradient(circle at 20px 20px, white 2px, transparent 0)',
              backgroundSize: '40px 40px',
            }} 
          />
          
          <Typography 
            variant="h5" 
            sx={{
              fontWeight: 700, 
              fontSize: { xs: '1.3rem', sm: '1.5rem' },
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              textShadow: '0 2px 4px rgba(0,0,0,0.2)',
              position: 'relative',
              zIndex: 2,
            }}
          >
            <Box sx={{ 
              width: 52, 
              height: 52, 
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255,255,255,0.25)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            }}>
              <Settings sx={{ fontSize: '2rem', color: 'white' }} />
            </Box>
            Trading Settings
          </Typography>
          
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255,255,255,0.95)', 
              ml: 8.4, 
              mt: 1, 
              maxWidth: 500,
              textShadow: '0 1px 2px rgba(0,0,0,0.15)',
              position: 'relative',
              zIndex: 2,
              fontSize: '0.95rem',
              fontWeight: 500
            }}
          >
            Manage your default order and product settings
          </Typography>
        </Box>
        
        <Box sx={{ p: { xs: 3, sm: 4 }, backgroundColor: 'transparent' }}>
          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} md={6}>
              <Box sx={{ 
                height: '100%',
                p: { xs: 2.5, sm: 3 }, 
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                display: 'flex',
                flexDirection: 'column',
                boxShadow: `0 6px 20px ${alpha(theme.palette.common.black, 0.05)}`,
                transition: 'all 0.3s ease',
                position: 'relative',
                backdropFilter: 'blur(8px)',
                overflow: 'hidden',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5, gap: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.15),
                      color: theme.palette.primary.main,
                      width: 54,
                      height: 54,
                    }}
                  >
                    <Box component="span" sx={{ fontSize: '1.7rem', fontWeight: 700 }}>O</Box>
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: '1.1rem' }}>
                      Order Type
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                      Default order type for new trades
                    </Typography>
                  </Box>
                </Box>
                
                <Box sx={{ 
                  mt: 'auto', 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: alpha(theme.palette.background.default, 0.5),
                  borderRadius: 2,
                  p: 2,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                }}>
                  <Chip
                    label={userSettings?.order_type}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      height: 38,
                      backgroundColor: alpha(theme.palette.primary.main, 0.15),
                      color: theme.palette.primary.main,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                      '& .MuiChip-label': { px: 2 }
                    }}
                  />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ 
                height: '100%',
                p: { xs: 2.5, sm: 3 }, 
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.15)}`,
                display: 'flex',
                flexDirection: 'column',
                boxShadow: `0 6px 20px ${alpha(theme.palette.common.black, 0.05)}`,
                transition: 'all 0.3s ease',
                position: 'relative',
                backdropFilter: 'blur(8px)',
                overflow: 'hidden',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5, gap: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(theme.palette.secondary.main, 0.15),
                      color: theme.palette.secondary.main,
                      width: 54,
                      height: 54,
                    }}
                  >
                    <Box component="span" sx={{ fontSize: '1.7rem', fontWeight: 700 }}>P</Box>
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, fontSize: '1.1rem' }}>
                      Product Type
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                      Default product type for trading
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ 
                  mt: 'auto', 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: alpha(theme.palette.background.default, 0.5),
                  borderRadius: 2,
                  p: 2,
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`
                }}>
                  <Chip
                    label={formatProduct(userSettings?.product_type)}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      height: 38,
                      backgroundColor: alpha(theme.palette.secondary.main, 0.15),
                      color: theme.palette.secondary.main,
                      border: `1px solid ${alpha(theme.palette.secondary.main, 0.25)}`,
                      '& .MuiChip-label': { px: 2 }
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3.5 }}>
            <Button
              variant="contained"
              onClick={handleOpenSettingsDialog}
              sx={{
                py: 1.2,
                px: 3.5,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 10,
                boxShadow: `0 6px 14px ${alpha(theme.palette.primary.main, 0.25)}`,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                fontSize: '0.95rem',
                '&:hover': {
                  boxShadow: `0 8px 18px ${alpha(theme.palette.primary.main, 0.35)}`,
                  background: `linear-gradient(90deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                }
              }}
              startIcon={
                <Avatar sx={{ 
                  width: 24, 
                  height: 24, 
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  mr: -0.5 
                }}>
                  <Settings sx={{ fontSize: '0.9rem' }} />
                </Avatar>
              }
            >
              Edit Settings
            </Button>
          </Box>
        </Box>
      </Card>
    </Container>
  );
};

export default PreferencesTab; 